const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/** Timeout em ms para requisições GET; evita ficar pendente se o backend não responder */
const GET_REQUEST_TIMEOUT_MS = 15000;

class ApiService {
  private getAuthHeader() {
    return {
      'Content-Type': 'application/json'
    };
  }

  private getFormDataAuthHeader() {
    return {};
  }

  // Generic HTTP methods
  async get(url: string, options?: { params?: any }) {
    let fullUrl = `${API_BASE_URL}${url}`;

    if (options?.params) {
      const searchParams = new URLSearchParams();
      Object.keys(options.params).forEach(key => {
        if (options.params[key] !== undefined) {
          searchParams.append(key, options.params[key]);
        }
      });
      if (searchParams.toString()) {
        fullUrl += `?${searchParams.toString()}`;
      }
    }

    console.log('🔍 [ApiService] GET Request:', {
      url: fullUrl,
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GET_REQUEST_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(fullUrl, {
        method: 'GET',
        headers: this.getAuthHeader(),
        credentials: 'include',
        signal: controller.signal
      });
    } catch (err: any) {
      clearTimeout(timeoutId);
      if (err?.name === 'AbortError') {
        console.warn('⏱️ [ApiService] GET timeout:', fullUrl);
        throw new Error('A requisição demorou muito. Tente novamente.');
      }
      throw err;
    }
    clearTimeout(timeoutId);

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    console.log('🔍 [ApiService] GET Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      contentType,
      isJson,
      url: fullUrl,
    });

    // Ler o corpo da resposta como texto primeiro para verificar o tipo
    const responseText = await response.text();

    if (!isJson) {
      console.error('❌ [ApiService] Resposta não é JSON:', {
        contentType,
        preview: responseText.substring(0, 200),
        url: fullUrl,
      });

      // Se não for JSON e o status não for OK, é um erro
      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}. Resposta: ${responseText.substring(0, 100)}`);
      }

      // Se não for JSON mas o status for OK, tentar fazer parse mesmo assim
      try {
        const data = JSON.parse(responseText);
        console.log('✅ [ApiService] GET Success (parseado de texto):', data);
        return data;
      } catch (parseError) {
        throw new Error(`Resposta não é JSON válido. Recebido: ${responseText.substring(0, 100)}`);
      }
    }

    if (!response.ok) {
      try {
        const error = JSON.parse(responseText);
        console.error('❌ [ApiService] GET Error:', error);
        throw new Error(error.error || error.message || 'Erro na requisição');
      } catch (parseError) {
        console.error('❌ [ApiService] Erro ao parsear JSON de erro:', responseText.substring(0, 200));
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }
    }

    try {
      const data = JSON.parse(responseText);
      console.log('✅ [ApiService] GET Success:', data);
      return data;
    } catch (parseError) {
      console.error('❌ [ApiService] Erro ao parsear JSON:', responseText.substring(0, 200));
      throw new Error('Resposta não é JSON válido');
    }
  }

  async post(url: string, data?: any, options?: { headers?: any }) {
    const isFormData = data instanceof FormData;
    const headers = isFormData ? this.getFormDataAuthHeader() : this.getAuthHeader();

    // Override headers if provided, but don't override Content-Type for FormData
    if (options?.headers) {
      if (isFormData) {
        // Para FormData, apenas adicionar headers que não sejam Content-Type
        Object.keys(options.headers).forEach(key => {
          if (key.toLowerCase() !== 'content-type') {
            headers[key] = options.headers[key];
          }
        });
      } else {
        Object.assign(headers, options.headers);
      }
    }

    console.log('🔐 Fazendo requisição POST:', {
      url: `${API_BASE_URL}${url}`,
      headers,
      hasCredentials: true,
      isFormData,
      contentType: headers['Content-Type'],
      dataType: typeof data,
      dataPreview: isFormData ? 'FormData' : JSON.stringify(data).substring(0, 100)
    });

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers,
      body: isFormData ? data : JSON.stringify(data),
      credentials: 'include'
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json();
        throw new Error(error.error || 'Erro na requisição');
      } else {
        // Se não for JSON, pode ser HTML de erro
        const errorText = await response.text();
        console.error('❌ Resposta não-JSON recebida:', errorText);
        throw new Error(`Erro ${response.status}: Servidor retornou HTML em vez de JSON`);
      }
    }

    return response.json();
  }

  async put(url: string, data?: any) {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      body: JSON.stringify(data),
      credentials: 'include'
    });

    if (!response.ok) {
      let errorMessage = `Erro ${response.status}: ${response.statusText}`;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          errorMessage = error.error || error.message || errorMessage;
        } else {
          const text = await response.text();
          if (text && text.length < 200) errorMessage = text;
        }
      } catch (_) {
        // usar errorMessage já definido
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async delete(url: string) {
    console.log('🗑️ [ApiService] DELETE Request:', {
      url: `${API_BASE_URL}${url}`,
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'DELETE',
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    console.log('🗑️ [ApiService] DELETE Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      url: `${API_BASE_URL}${url}`
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      let error;

      if (contentType && contentType.includes('application/json')) {
        try {
          error = await response.json();
          console.error('❌ [ApiService] DELETE Error (JSON):', error);
        } catch (parseError) {
          const errorText = await response.text();
          console.error('❌ [ApiService] DELETE Error (text):', errorText);
          throw new Error(`Erro ${response.status}: ${response.statusText}. Resposta: ${errorText.substring(0, 100)}`);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ [ApiService] DELETE Error (non-JSON):', errorText);
        throw new Error(`Erro ${response.status}: ${response.statusText}. Resposta: ${errorText.substring(0, 100)}`);
      }

      throw new Error(error?.error || error?.message || 'Erro ao deletar');
    }

    // Tentar parsear JSON, mas não falhar se a resposta estiver vazia
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        const data = await response.json();
        console.log('✅ [ApiService] DELETE Success:', data);
        return data;
      } catch (parseError) {
        // Se não conseguir parsear, retornar objeto vazio
        console.log('⚠️ [ApiService] DELETE Success (empty response)');
        return {};
      }
    }

    console.log('✅ [ApiService] DELETE Success (no content)');
    return {};
  }

  // Métodos diretos que não usam o prefixo /api
  async getDirect(url: string, options?: { params?: any }) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || 'http://localhost:4000';
    let fullUrl = `${baseUrl}${url}`;

    if (options?.params) {
      const searchParams = new URLSearchParams();
      Object.keys(options.params).forEach(key => {
        if (options.params[key] !== undefined) {
          searchParams.append(key, options.params[key]);
        }
      });
      if (searchParams.toString()) {
        fullUrl += `?${searchParams.toString()}`;
      }
    }

    console.log('🔍 [ApiService] GET Direct Request:', {
      url: fullUrl,
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    console.log('🔍 [ApiService] GET Direct Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('❌ [ApiService] GET Direct Error:', error);
      throw new Error(error.error || 'Erro na requisição');
    }

    const data = await response.json();
    console.log('✅ [ApiService] GET Direct Success:', data);
    return data;
  }

  async postDirect(url: string, data?: any, options?: { headers?: any }) {
    const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || 'http://localhost:4000';
    const isFormData = data instanceof FormData;
    const headers = isFormData ? this.getFormDataAuthHeader() : this.getAuthHeader();

    // Override headers if provided, but don't override Content-Type for FormData
    if (options?.headers) {
      if (isFormData) {
        // Para FormData, apenas adicionar headers que não sejam Content-Type
        Object.keys(options.headers).forEach(key => {
          if (key.toLowerCase() !== 'content-type') {
            headers[key] = options.headers[key];
          }
        });
      } else {
        Object.assign(headers, options.headers);
      }
    }

    console.log('🔐 Fazendo requisição POST Direct:', {
      url: `${baseUrl}${url}`,
      headers,
      hasCredentials: true,
      isFormData,
      contentType: headers['Content-Type'],
      dataType: typeof data,
      dataPreview: isFormData ? 'FormData' : JSON.stringify(data).substring(0, 100)
    });

    const response = await fetch(`${baseUrl}${url}`, {
      method: 'POST',
      headers,
      body: isFormData ? data : JSON.stringify(data),
      credentials: 'include'
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json();
        throw new Error(error.error || 'Erro na requisição');
      } else {
        // Se não for JSON, pode ser HTML de erro
        const errorText = await response.text();
        console.error('❌ Resposta não-JSON recebida:', errorText);
        throw new Error(`Erro ${response.status}: Servidor retornou HTML em vez de JSON`);
      }
    }

    return response.json();
  }

  // Auth methods
  async login(email: string, password: string) {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password }),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro no login');
    }

    return response.json();
  }

  async me() {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao buscar dados do usuário');
    }

    return response.json();
  }

  async register(userData: any) {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      credentials: 'include',
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao registrar usuário');
    }

    return response.json();
  }

  // User management methods
  async getUsers() {
    const response = await fetch(`${API_BASE_URL}/auth/users`, {
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao buscar usuários');
    }

    return response.json();
  }

  async createUser(userData: {
    name: string;
    email: string;
    password: string;
    role: 'admin' | 'cliente' | 'analista';
    client_id?: number;
    allowed_areas?: string[];
  }) {
    const response = await fetch(`${API_BASE_URL}/auth/users`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      credentials: 'include',
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao criar usuário');
    }

    return response.json();
  }

  async updateUser(id: number, userData: {
    name?: string;
    email?: string;
    password?: string;
    role?: 'admin' | 'cliente' | 'analista';
    client_id?: number;
    allowed_areas?: string[];
  }) {
    const response = await fetch(`${API_BASE_URL}/auth/users/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      credentials: 'include',
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao atualizar usuário');
    }

    return response.json();
  }

  async toggleUserStatus(id: number) {
    const response = await fetch(`${API_BASE_URL}/auth/users/${id}/toggle-status`, {
      method: 'PATCH',
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao alterar status do usuário');
    }

    return response.json();
  }

  async deleteUser(id: number) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/users/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeader(),
        credentials: 'include'
      });

      if (!response.ok) {
        let errorMessage = 'Erro ao deletar usuário';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // Se não conseguir parsear JSON, usar mensagem padrão
          errorMessage = `Erro ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error: any) {
      console.error('❌ [API] Erro ao deletar usuário:', error);
      throw error;
    }
  }

  // Client methods
  async getClients() {
    try {
      const response = await fetch(`${API_BASE_URL}/clients`, {
        headers: this.getAuthHeader(),
        credentials: 'include'
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { error: `Erro ${response.status}: ${response.statusText}` };
        }
        console.error('❌ [ApiService.getClients] Erro na resposta:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error || 'Erro ao buscar clientes');
      }

      const data = await response.json();
      console.log('✅ [ApiService.getClients] Dados recebidos:', data);

      // O backend retorna { success: true, clients: [...] }
      // Verificar primeiro a estrutura mais comum
      if (data.clients && Array.isArray(data.clients)) {
        console.log(`✅ [ApiService.getClients] ${data.clients.length} clientes extraídos de data.clients`);
        return data.clients;
      }

      // Fallback para estrutura alternativa (compatibilidade)
      if (data.data && Array.isArray(data.data)) {
        console.log(`✅ [ApiService.getClients] ${data.data.length} clientes extraídos de data.data`);
        return data.data;
      }

      // Se por algum motivo não tiver a estrutura esperada, logar e retornar array vazio
      console.error('⚠️ [ApiService.getClients] Estrutura de dados inesperada:', {
        data,
        hasClients: !!data.clients,
        hasData: !!data.data,
        success: data.success,
        keys: Object.keys(data)
      });
      return [];
    } catch (error: any) {
      console.error('❌ [ApiService.getClients] Erro ao buscar clientes:', error);
      console.error('❌ [ApiService.getClients] Detalhes do erro:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      throw error; // Re-lançar o erro para que o componente possa tratá-lo
    }
  }

  async createClient(clientData: any) {
    const response = await fetch(`${API_BASE_URL}/clients`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      credentials: 'include',
      body: JSON.stringify(clientData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao criar cliente');
    }

    return response.json();
  }

  async updateClient(id: number, clientData: any) {
    const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      credentials: 'include',
      body: JSON.stringify(clientData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao atualizar cliente');
    }

    return response.json();
  }

  async deleteClient(id: number) {
    const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao deletar cliente');
    }

    return response.json();
  }

  // ===== CLIENT EVENT STATUS API =====

  /**
   * Buscar status de eventos com filtros opcionais
   */
  async getClientEventStatuses(params?: {
    clientId?: number;
    startDate?: string;
    endDate?: string;
    year?: number;
    month?: number;
    billingRuleId?: number;
  }) {
    const response = await this.get('/client-event-statuses', { params });

    if (!response.success) {
      throw new Error(response.error || 'Erro ao buscar status dos eventos');
    }

    return response.data;
  }

  /**
   * Criar ou atualizar status de um evento
   */
  async updateClientEventStatus(data: {
    clientId: number;
    billingRuleId: number;
    eventDate: string;
    isInProgress?: boolean;
    isFinalized?: boolean;
  }) {
    const response = await fetch(`${API_BASE_URL}/client-event-statuses`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      credentials: 'include',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao salvar status do evento');
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Erro ao salvar status do evento');
    }

    return result.data;
  }

  /**
   * Atualizar status específico por ID
   */
  async updateClientEventStatusById(id: number, data: {
    isInProgress?: boolean;
    isFinalized?: boolean;
  }) {
    const response = await fetch(`${API_BASE_URL}/client-event-statuses/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeader(),
      credentials: 'include',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao atualizar status do evento');
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Erro ao atualizar status do evento');
    }

    return result.data;
  }

  /**
   * Remover status de um evento
   */
  async deleteClientEventStatus(params: {
    clientId: number;
    billingRuleId: number;
    eventDate: string;
  }) {
    const response = await fetch(`${API_BASE_URL}/client-event-statuses?${new URLSearchParams({
      clientId: params.clientId.toString(),
      billingRuleId: params.billingRuleId.toString(),
      eventDate: params.eventDate
    })}`, {
      method: 'DELETE',
      headers: this.getAuthHeader(),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao remover status do evento');
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Erro ao remover status do evento');
    }

    return result;
  }
}

const api = new ApiService();
export default api;
export const apiService = api; 